import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  getPendingActions,
  approveAction,
  rejectAction,
  type AgentActionApi,
} from '@/services/api/agents'

const riskLevelColor: Record<string, string> = {
  low: '#13AB5B',
  medium: '#E6A23C',
  high: '#E93323',
  critical: '#B91C1C',
}

export default function AIActionsPage() {
  const [actions, setActions] = useState<AgentActionApi[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [processingId, setProcessingId] = useState<string | null>(null)

  const fetchActions = async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await getPendingActions()
      setActions(list)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load pending actions')
      setActions([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActions()
  }, [])

  const handleApprove = async (actionId: string) => {
    setProcessingId(actionId)
    setError(null)
    try {
      await approveAction(actionId)
      await fetchActions()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to approve')
    } finally {
      setProcessingId(null)
    }
  }

  const handleReject = async (actionId: string) => {
    setProcessingId(actionId)
    setError(null)
    try {
      await rejectAction(actionId)
      await fetchActions()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to reject')
    } finally {
      setProcessingId(null)
    }
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1
        className="text-2xl font-semibold mb-4"
        style={{ fontFamily: 'Lexend Deca', color: '#212126' }}
      >
        AI Actions
      </h1>
      <p className="text-sm text-gray-500 mb-4">
        Pending AI recommendations. Approve to execute or reject.
      </p>
      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 text-red-700 text-sm">{error}</div>
      )}
      {loading && <p className="text-gray-500">Loading…</p>}
      {!loading && actions.length === 0 && (
        <p className="text-gray-500">No pending recommendations.</p>
      )}
      {!loading && actions.length > 0 && (
        <ul className="space-y-4">
          {actions.map((action) => {
            const run = action.agentRun
            const aiOutput = run?.aiOutput as { summary?: string; reasoning?: string; riskLevel?: string } | undefined
            const summary = aiOutput?.reasoning ?? aiOutput?.summary ?? ''
            const riskLevel = aiOutput?.riskLevel ?? 'low'
            const viewUrl =
              run?.entityType === 'shipment'
                ? `/shipments?highlight=${encodeURIComponent(run.entityId ?? '')}`
                : run?.entityType === 'clearing'
                  ? `/clearing?highlight=${encodeURIComponent(run.entityId ?? '')}`
                  : null
            return (
              <li
                key={action.id}
                className="border border-gray-200 rounded-lg p-4 bg-white shadow-sm"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900">
                      {(action.payload?.description ?? action.payload?.message) as string || `${action.actionType}${action.target ? ` → ${action.target}` : ''}`}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">{summary}</p>
                    <div className="flex items-center gap-2 mt-2">
                      <span
                        className="text-xs font-medium"
                        style={{ color: riskLevelColor[riskLevel] || riskLevelColor.low }}
                      >
                        {riskLevel}
                      </span>
                      <span className="text-xs text-gray-400">
                        {run?.entityType} · {run?.entityId?.slice(0, 8)}…
                      </span>
                      {viewUrl && (
                        <Link
                          to={viewUrl}
                          className="text-xs text-blue-600 hover:underline"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      type="button"
                      disabled={processingId === action.id}
                      onClick={() => handleApprove(action.id)}
                      className="px-3 py-1.5 rounded text-sm font-medium bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      disabled={processingId === action.id}
                      onClick={() => handleReject(action.id)}
                      className="px-3 py-1.5 rounded text-sm font-medium bg-gray-500 text-white hover:bg-gray-600 disabled:opacity-50"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </li>
            )
          })}
        </ul>
      )}
      <p className="text-xs text-gray-500 mt-6">
        This is an AI-generated assistant. Please verify critical decisions before approving.
      </p>
    </div>
  )
}
